/**
 * Proxy-first asset upgrade (q-desktop-proxy-first-asset-upgrade). Desktop full quality
 * used to hold the loading screen on every textured base in view: 275 MB on the guest
 * demo's first visit, playable at 24 s on a fast line and only by the 30 s readiness
 * timeout at 20 Mbit (2026-11-01, Edge, cold cache). The low tier's untextured proxies
 * (LOD0) put the same world on screen with ~20 MB in about half the time.
 *
 * So desktop now draws the proxies first and swaps in the textured bases once the
 * loading screen has lifted: the session's FIDELITY is unchanged (`fullDetailAssetsActive`
 * still says full), only the ORDER of the downloads. The gate opens once, when the
 * readiness latch goes ready; the queue then upgrades the nearest objects to the camera
 * first, a few at a time, so the game's own traffic is never starved.
 *
 * Off (released from construction, every caller behaves exactly as before) on the low
 * tier (it never upgrades), in capture mode (art review wants the textured art at the
 * reveal), with `?assets=full`, and when there is no loading screen to wait for.
 */

/** Textured bases loading at once after the reveal. */
export const UPGRADE_CONCURRENCY = 4;

/** Whether this session draws proxies first. Pure: the Game passes the session's facts. */
export function proxyFirstEnabled(options: {
  /** The session's quality profile is low (mobile or `?quality=low`): it never upgrades. */
  readonly qualityLow: boolean;
  /** `capturedTexturesPreference()`: non-null in capture mode, which keeps its choice. */
  readonly capturePreference: boolean | null;
  /** The effective search string (`?assets=full` opts out). */
  readonly search: string;
  /** A loading screen holds the reveal (a readiness session exists). */
  readonly hasLoadingScreen: boolean;
}): boolean {
  if (options.qualityLow || options.capturePreference !== null || !options.hasLoadingScreen)
    return false;
  try {
    return new URLSearchParams(options.search).get('assets') !== 'full';
  } catch {
    return true;
  }
}

/** The one-shot gate: closed while the loading screen is up (when enabled), then open. */
export class AssetUpgradeGate {
  private releasedFlag: boolean;
  private releasedAt: number | null = null;
  private readonly listeners: (() => void)[] = [];

  constructor(options: { enabled: boolean }) {
    this.releasedFlag = !options.enabled;
  }

  /** Whether textured bases may load now. */
  get released(): boolean {
    return this.releasedFlag;
  }

  /** `performance.now()` at release, or null (never held, or still holding). */
  get releasedAtMs(): number | null {
    return this.releasedAt;
  }

  /** Run `listener` once at release — at once if already released. */
  onRelease(listener: () => void): void {
    if (this.releasedFlag) listener();
    else this.listeners.push(listener);
  }

  /** Open the gate (the loading screen lifted). Idempotent. */
  release(nowMs: number): void {
    if (this.releasedFlag) return;
    this.releasedFlag = true;
    this.releasedAt = nowMs;
    for (const listener of this.listeners.splice(0)) listener();
  }
}

/** One deferred upgrade: where it is (for nearest-first) and the work. */
interface UpgradeJob {
  readonly x: number;
  readonly z: number;
  readonly run: () => Promise<unknown>;
}

/**
 * Upgrades waiting for the gate, run nearest-first with at most `concurrency` in flight.
 * Jobs added after `start` join the queue in distance order from the same eye. A job
 * guards its own staleness (a zone change mid-upgrade): the queue only sequences.
 */
export class UpgradeQueue {
  private readonly waiting: UpgradeJob[] = [];
  private eye: { x: number; z: number } | null = null;
  private inFlight = 0;
  private finished = 0;

  constructor(private readonly concurrency = UPGRADE_CONCURRENCY) {}

  add(x: number, z: number, run: () => Promise<unknown>): void {
    this.waiting.push({ x, z, run });
    if (this.eye !== null) this.pump();
  }

  /** Begin draining, nearest to `eye` first. Further calls are no-ops. */
  start(eye: { x: number; z: number }): void {
    if (this.eye !== null) return;
    this.eye = { x: eye.x, z: eye.z };
    this.pump();
  }

  /** Drop everything still waiting (a new world build supersedes it). */
  clear(): void {
    this.waiting.length = 0;
  }

  /** For the scene dump and tests: started, waiting, running, finished. */
  get state(): { started: boolean; waiting: number; running: number; finished: number } {
    return {
      started: this.eye !== null,
      waiting: this.waiting.length,
      running: this.inFlight,
      finished: this.finished,
    };
  }

  private pump(): void {
    const eye = this.eye;
    if (eye === null) return;
    while (this.inFlight < this.concurrency && this.waiting.length > 0) {
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < this.waiting.length; i += 1) {
        const job = this.waiting[i]!;
        const dist = Math.hypot(job.x - eye.x, job.z - eye.z);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      const [job] = this.waiting.splice(best, 1);
      this.inFlight += 1;
      void Promise.resolve()
        .then(job!.run)
        .catch(() => undefined) // each job warns for itself; a failure never stalls the queue
        .finally(() => {
          this.inFlight -= 1;
          this.finished += 1;
          this.pump();
        });
    }
  }
}
